import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, CreditCard, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { billingApi } from '@/lib/billing-api';
import { catalogApi } from '@/lib/catalog-api';
import { getErrorMessage } from '@/lib/errors';
import type { GatewayOption, Plan, Product } from '@/types/api';

/**
 * The order plans are shown in, and which is highlighted.
 *
 * By `sort_order` from the catalogue rather than by price, because an operator who reorders plans
 * in the admin screen expects that to be what customers see. Price ordering would quietly ignore
 * them.
 */
const RECOMMENDED = 'pro';

/**
 * The order entitlements are listed in, on every card.
 *
 * Fixed rather than sorted, because the whole point of three cards side by side is reading across
 * them. Sorting alphabetically put "10 Monitors" first on Starter and "10 Servers" first on Pro —
 * the numbers sort as text — so the same row of three cards showed three different things and
 * nothing could be compared at a glance.
 *
 * Anything not named here follows, so a fifth entitlement added later still appears rather than
 * being silently dropped.
 */
const ENTITLEMENT_ORDER = ['servers', 'monitors', 'team_members'];

/**
 * Symbols for the currencies the platform prices in.
 *
 * `price_formatted` from the API is the number alone — "12.00" — because the API has no opinion
 * about presentation. A price on a card with no currency on it is the kind of detail that makes
 * somebody hesitate at exactly the wrong moment, so the symbol is added here and the ISO code is
 * the fallback for anything unlisted.
 */
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
};

function money(amount: string, currency: string): string {
  const symbol = CURRENCY_SYMBOLS[currency.toUpperCase()];

  return symbol === undefined ? `${currency} ${amount}` : `${symbol}${amount}`;
}

/** `api_access` reads as "Api Access" under CSS capitalisation, which is not how anybody writes it. */
function label(key: string): string {
  const words = key.replace(/_/g, ' ');

  return words.replace(/\bapi\b/gi, 'API');
}

interface Props {
  gateways: GatewayOption[];
  /** True when the customer already has a live subscription — the wording changes, not the flow. */
  hasSubscription: boolean;
  /** True while the free trial is running, which buying a plan ends. */
  onTrial?: boolean;
}

/**
 * Lets a customer buy a plan.
 *
 * ## Why this did not exist until now
 *
 * The endpoint, the gateways and the subscription chain were all built and tested. The
 * button was not. A customer could sign up and the billing page told them to "choose a plan" while
 * offering none — every subscription on the platform had to be assigned by an administrator.
 *
 * ## Interval is a toggle, not six cards
 *
 * The catalogue holds each plan twice, once per billing interval. Showing all six side by side
 * makes the customer compare on two axes at once. The toggle picks the interval; the cards compare
 * the tiers.
 *
 * ## Entitlements are the feature list
 *
 * Read from the plan's own `features` rather than from marketing copy. The pricing page and the
 * dashboard would otherwise be two claims about the same plan, and the one that governs what the
 * customer can actually do is this one.
 */
export function ChoosePlanPanel({ gateways, hasSubscription, onTrial = false }: Props) {
  const [interval, setInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [starting, setStarting] = useState<string | null>(null);

  const products = useQuery({ queryKey: ['catalog-products'], queryFn: catalogApi.listProducts });

  const checkout = useMutation({
    mutationFn: (planId: string) => billingApi.startCheckout(planId, gateways[0]?.identifier ?? ''),
    onSuccess: (session) => {
      // A full navigation rather than a new tab: the gateway sends the customer back to the
      // success URL when they are done, and a popup would strand that return in a window the
      // dashboard is not watching.
      window.location.href = session.checkout_url;
    },
    onError: () => setStarting(null),
  });

  const plans = useMemo(() => {
    const all = (products.data ?? []).flatMap((product: Product) => product.plans);

    return all
      .filter((plan) => plan.status === 'active' && plan.billing_interval === interval)
      .sort((a, b) => a.sort_order - b.sort_order);
  }, [products.data, interval]);

  const intervalsAvailable = useMemo(() => {
    const all = (products.data ?? []).flatMap((product: Product) => product.plans);

    return {
      monthly: all.some((p) => p.status === 'active' && p.billing_interval === 'monthly'),
      yearly: all.some((p) => p.status === 'active' && p.billing_interval === 'yearly'),
    };
  }, [products.data]);

  /**
   * How many months a year of billing saves — read from the published plans, never assumed.
   *
   * The marketing page can say "2 months free" because it renders from a constant it owns. This
   * panel renders whatever is in the catalogue, and an operator who reprices a yearly plan would
   * turn a hard-coded claim here into the same kind of lie the pricing page was just corrected for:
   * a saving advertised on the screen where the customer is about to pay, that the invoice does not
   * honour.
   *
   * So it is derived, and only shown when every tier that offers both cadences agrees on the same
   * whole number of months. Anything else — mixed savings, an odd multiplier, one tier repriced —
   * shows nothing rather than something approximate.
   */
  const monthsSaved = useMemo(() => {
    const active = (products.data ?? [])
      .flatMap((product: Product) => product.plans)
      .filter((plan) => plan.status === 'active');

    const savings = new Set<number>();

    for (const yearly of active.filter((plan) => plan.billing_interval === 'yearly')) {
      // Paired on the slug with its cadence stripped, so `pro` and `pro-monthly` both match the
      // `pro-yearly` they belong to.
      const tier = yearly.slug.replace(/-yearly$/, '');
      const monthly = active.find(
        (plan) =>
          plan.billing_interval === 'monthly'
          && plan.currency === yearly.currency
          && plan.slug.replace(/-monthly$/, '') === tier,
      );

      if (monthly === undefined || monthly.price_minor <= 0) {
        return null;
      }

      const months = yearly.price_minor / monthly.price_minor;

      if (!Number.isInteger(months) || months >= 12 || months < 1) {
        return null;
      }

      savings.add(12 - months);
    }

    return savings.size === 1 ? [...savings][0] : null;
  }, [products.data]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{hasSubscription ? 'Change plan' : 'Choose a plan'}</CardTitle>
        <CardDescription>
          {hasSubscription
            ? 'Buying a different plan starts a new subscription. Cancel the current one afterwards if you no longer need it.'
            : onTrial
              ? 'Buying a plan ends the free trial as soon as the payment is confirmed. Everything you set up during the trial stays as it is, with the new plan’s limits.'
              : 'Every plan includes the whole platform. What changes is how much of it you can use.'}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* ## A missing gateway disables the button, not the page
            //
            // Returning early here — which this did — meant a customer saw nothing at all on an
            // installation whose operator had not finished connecting a payment provider. Nothing
            // is the worst of the three options: the plans are real, they are what the pricing page
            // advertises, and hiding them makes the product look broken rather than unfinished. */}
        {gateways.length === 0 && (
          <Alert variant="warning">
            No payment method is connected yet, so plans cannot be bought from here. If you are the
            operator, set a gateway's credentials and run{' '}
            <code className="font-mono">config:check</code>.
          </Alert>
        )}

        {checkout.error !== null && (
          <Alert variant="danger">{getErrorMessage(checkout.error)}</Alert>
        )}
        {products.isError && <Alert variant="danger">{getErrorMessage(products.error)}</Alert>}

        {products.isLoading && (
          <p className="text-muted-foreground flex items-center gap-2 text-sm">
            <Loader2 className="size-4 animate-spin" /> Loading plans…
          </p>
        )}

        {intervalsAvailable.monthly && intervalsAvailable.yearly && (
          <div className="flex justify-center">
            <div
              className="border-border/60 bg-muted/20 inline-flex rounded-full border p-1"
              role="group"
              aria-label="Billing period"
            >
              {(['monthly', 'yearly'] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setInterval(option)}
                  aria-pressed={interval === option}
                  className={`rounded-full px-4 py-1.5 text-sm capitalize transition-colors ${
                    interval === option
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {option}
                  {option === 'yearly' && monthsSaved !== null && (
                    <span
                      className={
                        interval === 'yearly' ? 'ml-1.5 opacity-80' : 'ml-1.5 text-emerald-500'
                      }
                    >
                      {monthsSaved} months free
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {!products.isLoading && plans.length === 0 && (
          <Alert variant="info">
            No plans are published for this billing period yet.
          </Alert>
        )}

        <div className="grid gap-4 md:grid-cols-3">
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              busy={checkout.isPending || gateways.length === 0}
              starting={starting === plan.id}
              onChoose={() => {
                setStarting(plan.id);
                checkout.mutate(plan.id);
              }}
            />
          ))}
        </div>

        {gateways.length > 0 && (
          <p className="text-muted-foreground text-center text-xs">
            Payment is handled by {gateways[0].display_name}. You will be taken to their secure
            checkout. This platform never sees your card details.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * One plan.
 *
 * The entitlement list comes from the plan itself. `-1` is the platform's sentinel for no ceiling
 * and has to be rendered as "Unlimited" — showing "-1 servers" is the kind of raw sentinel that
 * makes a customer distrust the whole page.
 */
function PlanCard({
  plan,
  busy,
  starting,
  onChoose,
}: {
  plan: Plan;
  busy: boolean;
  starting: boolean;
  onChoose: () => void;
}) {
  const highlighted = plan.slug.startsWith(RECOMMENDED);

  const numeric = Object.entries(plan.features).filter(([, limit]) => typeof limit === 'number');

  const entitlements = [...numeric]
    .sort(([a], [b]) => {
      const left = ENTITLEMENT_ORDER.indexOf(a);
      const right = ENTITLEMENT_ORDER.indexOf(b);

      // Unlisted keys sort after the known ones, and alphabetically among themselves.
      return (left === -1 ? ENTITLEMENT_ORDER.length : left)
        - (right === -1 ? ENTITLEMENT_ORDER.length : right)
        || a.localeCompare(b);
    })
    .map(([key, limit]) => `${limit === -1 ? 'Unlimited' : limit} ${label(key)}`);

  const capabilities = Object.entries(plan.features)
    .filter(([, limit]) => limit === true)
    .map(([key]) => label(key));

  return (
    <div
      className={`relative flex flex-col rounded-xl border p-5 ${
        highlighted ? 'border-primary bg-primary/5' : 'border-border/60'
      }`}
    >
      {highlighted && (
        <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2">Recommended</Badge>
      )}

      <p className="font-medium">{plan.name}</p>
      <p className="mt-2 text-2xl font-semibold tracking-tight">
        {money(plan.price_formatted, plan.currency)}
        <span className="text-muted-foreground ml-1 text-sm font-normal">
          {plan.billing_interval === 'yearly' ? 'per year' : 'per month'}
        </span>
      </p>
      {/* "$490 a year" and "$49 a month" are the same decision, and a customer should not have to
          divide to compare them. Computed from this plan's own price, so it cannot disagree with
          the figure printed directly above it. */}
      {plan.billing_interval === 'yearly' && (
        <p className="text-muted-foreground mt-1 text-xs">
          {money((plan.price_minor / 12 / 100).toFixed(2), plan.currency)} a month, billed yearly
        </p>
      )}
      {plan.description !== '' && (
        <p className="text-muted-foreground mt-2 text-xs">{plan.description}</p>
      )}

      <ul className="mt-4 flex-1 space-y-1.5">
        {[...entitlements, ...capabilities].map((line) => (
          <li key={line} className="flex items-start gap-2 text-sm first-letter:uppercase">
            <Check className="mt-0.5 size-3.5 shrink-0 text-emerald-500" aria-hidden />
            {line}
          </li>
        ))}
      </ul>

      <Button
        className="mt-5 w-full"
        variant={highlighted ? 'default' : 'outline'}
        onClick={onChoose}
        disabled={busy}
      >
        {starting ? (
          <Loader2 className="mr-2 size-4 animate-spin" />
        ) : (
          <CreditCard className="mr-2 size-4" />
        )}
        Choose {plan.name}
      </Button>
    </div>
  );
}
